import ClassMethodForm from './ClassMethodForm';
import GPMotionControls from './GPMotionControls';
import ParameterInput from './ParameterInput';
import RequestPreview from './RequestPreview';
import ResponseDisplay from './ResponseDisplay';
import { useGPMotion } from './useGPMotion';

import {
  GPMOTION_METHOD_CONFIG,
  GPMOTION_METHODS,
  VERSION_1,
} from '@/constant';
import { useJsBridgeVersion } from '@/context/JsBridgeVersionContext';
import { useSDKHandler } from '@/features/method-tester/hooks';
import { useSDKMethodForm } from '@/features/method-tester/hooks/useSDKMethodForm';
import { NewSDKCallResult } from '@/features/method-tester/types';
import { capitalizeFirst, isGPMotion } from '@/utils';
import { Button } from '@asphalt-react/button';

const IndividualTesterWidget: React.FC = () => {
  const { version } = useJsBridgeVersion();
  const { response, invokeSDKActionV1, clearResponse } =
    useSDKHandler<NewSDKCallResult>();
  const formData = useSDKMethodForm();
  const { formState, reset: resetForm, getSubmissionData } = formData;
  const { streamData, handleStart, handleStop, resetStreamData } = useGPMotion({
    getSubmissionData,
    invokeSDKActionV1,
    clearResponse,
  });

  const submissionData = getSubmissionData();
  const isGPMotionSelected = isGPMotion(submissionData.className);
  const canSubmit = Boolean(
    formState.data.className.trim() && formState.data.methodName.trim()
  );
  const isPreviewVisible = Boolean(
    formState.data.className.trim() ||
    formState.data.methodName.trim() ||
    Object.keys(formState.data.params).some((key) => key.trim())
  );

  const handleSubmit = async () => {
    try {
      const {
        methodName: originalMethodName,
        params: originalParams,
        className,
      } = getSubmissionData();

      clearResponse();

      if (version !== VERSION_1) {
        throw new Error('Unsupported SDK version');
      }

      let methodName = originalMethodName;
      let params = originalParams;

      if (isGPMotionSelected) {
        const config = GPMOTION_METHOD_CONFIG[methodName.toLowerCase()] || {};
        methodName = config.method ?? 'start' + capitalizeFirst(methodName);
        params = config.params ?? originalParams;
      }

      await invokeSDKActionV1(className, methodName, params);
    } catch (error) {
      console.error('SDK call error:', error);
    }
  };

  const handleReset = () => {
    resetForm();
    clearResponse();
    resetStreamData();
  };

  return (
    <div className="max-w-[800px] w-full">
      <ClassMethodForm formData={formData} gpMotionMethods={GPMOTION_METHODS} />
      <ParameterInput formData={formData} />
      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <Button
          data-testid="individual-test-method-button"
          onClick={handleSubmit}
          disabled={!canSubmit || isGPMotionSelected}
          size="m"
          stretch
        >
          Test Method
        </Button>
        <Button
          data-testid="individual-clear-all-button"
          onClick={handleReset}
          size="m"
          secondary
          stretch
        >
          Clear All
        </Button>
      </div>
      {isGPMotionSelected && (
        <GPMotionControls
          methodName={submissionData.methodName}
          onStart={handleStart}
          onStop={handleStop}
        />
      )}
      <RequestPreview data={submissionData} isVisible={isPreviewVisible} />
      <ResponseDisplay response={response} />
      {isGPMotionSelected && streamData[submissionData.methodName] && (
        <div className="mt-4" data-testid="individual-stream-data-container">
          <strong>Stream Data:</strong>
          <ResponseDisplay response={streamData[submissionData.methodName]} />
        </div>
      )}
    </div>
  );
};

export default IndividualTesterWidget;
